import React, { useState, useEffect } from 'react'
import AppDropdown from '../../../components/AppDropdown'
import AppCombobox from '../../../components/AppCombobox'
import AppToggle from '../../../components/AppToggle'
import AppCheckbox from '../../../components/AppCheckbox'
import {
  listWorkflowRules,
  updateWorkflowRule,
  listEmailTemplates,
  listUserOptions,
  listRoleOptions,
} from '../api/emailSettingsApi'

const WORKFLOW_MODULES = [
  { code: 'TIMESHEET', name: 'Timesheet Approvals', icon: 'schedule' },
  { code: 'LEAVE', name: 'Leave Approvals', icon: 'event_available' },
  { code: 'PRODUCTIVITY', name: 'Productivity Hours Approvals', icon: 'timer' },
  { code: 'COMPANY', name: 'Company Approvals', icon: 'domain' },
]

const RECIPIENT_OPTIONS = [
  { value: 'MANAGER', label: "Submitter's Direct Manager" },
  { value: 'MANAGER_OF_MANAGER', label: "Manager's Manager (Skip-Level Manager)" },
  { value: 'DEPARTMENT_HEAD', label: 'Department Head' },
  { value: 'SPECIFIC_USER', label: 'Specific User' },
  { value: 'SPECIFIC_ROLE', label: 'Specific Role Members' },
]

function formatApiErrorMessage(err, fallback = 'Operation failed.') {
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d))).join(', ')
  }
  if (detail && typeof detail === 'object') {
    return detail.message || detail.msg || JSON.stringify(detail)
  }
  return err?.message || fallback
}

export default function ApprovalWorkflowsTab() {
  const [activeModule, setActiveModule] = useState('TIMESHEET')
  const [rules, setRules] = useState([])
  const [templates, setTemplates] = useState([])
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState(null)

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    setLoading(true)
    setFeedback(null)
    try {
      const [rulesData, templatesData, usersData, rolesData] = await Promise.all([
        listWorkflowRules(),
        listEmailTemplates(),
        listUserOptions(),
        listRoleOptions(),
      ])
      setRules(rulesData)
      setTemplates(templatesData)
      setUsers(usersData)
      setRoles(rolesData)
    } catch (err) {
      setFeedback({
        type: 'error',
        message: 'Failed to load workflow configuration: ' + formatApiErrorMessage(err),
      })
    } finally {
      setLoading(false)
    }
  }

  const currentRule = rules.find((r) => (r.moduleCode || r.module_code) === activeModule) || {
    moduleCode: activeModule,
    moduleName: WORKFLOW_MODULES.find((m) => m.code === activeModule)?.name,
    isEnabled: true,
    notifyInitial: true,
    initialRecipientType: 'MANAGER',
    initialRecipientUserId: null,
    initialRecipientRoleId: null,
    initialTemplateCode: `${activeModule}_APPROVAL_INITIAL`,
    initialCcRequestor: false,
    l1Enabled: true,
    l1Hours: 4,
    l1RecipientType: 'MANAGER_OF_MANAGER',
    l1RecipientUserId: null,
    l1RecipientRoleId: null,
    l1TemplateCode: `${activeModule}_APPROVAL_L1`,
    l1CcOriginalApprover: true,
    l1CcRequestor: false,
    l2Enabled: false,
    l2Hours: 8,
    l2RecipientType: 'DEPARTMENT_HEAD',
    l2RecipientUserId: null,
    l2RecipientRoleId: null,
    l2TemplateCode: `${activeModule}_APPROVAL_L2`,
    l2CcOriginalApprover: true,
    l2CcRequestor: false,
    l3Enabled: false,
    l3Hours: 24,
    l3RecipientType: 'DEPARTMENT_HEAD',
    l3RecipientUserId: null,
    l3RecipientRoleId: null,
    l3TemplateCode: `${activeModule}_APPROVAL_L3`,
    l3CcOriginalApprover: true,
    l3CcRequestor: false,
  }

  function handleRuleFieldChange(field, value) {
    setRules((prev) => {
      const idx = prev.findIndex((r) => (r.moduleCode || r.module_code) === activeModule)
      if (idx >= 0) {
        const copy = [...prev]
        copy[idx] = { ...copy[idx], [field]: value }
        return copy
      } else {
        return [...prev, { ...currentRule, [field]: value }]
      }
    })
  }

  async function handleToggleMasterEngine(nextVal) {
    handleRuleFieldChange('isEnabled', nextVal)
    try {
      const updatedRule = { ...currentRule, isEnabled: nextVal }
      await updateWorkflowRule(activeModule, updatedRule)
      setFeedback({
        type: 'success',
        message: `Automated Notification Engine for "${currentRule.moduleName || activeModule}" is now ${nextVal ? 'Active' : 'Disabled'}.`,
      })
    } catch (err) {
      setFeedback({
        type: 'error',
        message: 'Failed to update module status: ' + formatApiErrorMessage(err),
      })
    }
  }

  async function handleSave() {
    setSaving(true)
    setFeedback(null)
    try {
      if (rules && rules.length > 0) {
        await Promise.all(
          rules.map((rule) =>
            updateWorkflowRule(rule.moduleCode || rule.module_code, rule)
          )
        )
      } else {
        await updateWorkflowRule(activeModule, currentRule)
      }
      setFeedback({
        type: 'success',
        message: 'All approval workflow rules saved successfully!',
      })
    } catch (err) {
      setFeedback({
        type: 'error',
        message: 'Error saving workflow rules: ' + formatApiErrorMessage(err),
      })
    } finally {
      setSaving(false)
    }
  }

  const templateOptions = templates.map((t) => ({
    value: t.templateCode || t.template_code,
    label: `${t.templateName || t.template_name} (${t.templateCode || t.template_code})`,
  }))

  const userOptions = users.map((u) => ({
    value: String(u.id),
    label: `${u.name} (${u.email || u.user_code || 'No email'})`,
  }))

  const roleOptions = roles.map((r) => ({
    value: String(r.id),
    label: r.name || r.role_name || `Role #${r.id}`,
  }))

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-16 rounded-3xl border border-outline-variant/25 bg-surface-container-lowest">
        <span className="material-symbols-outlined text-4xl text-primary animate-spin">sync</span>
        <p className="mt-3 text-xs text-on-surface-variant font-medium">Loading Approval Workflows...</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {/* ── Top Canopy: Header & Action ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/15 pb-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">
            SYSTEM &amp; COMMUNICATIONS
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Approval Notification &amp; Escalation Workflows
          </h1>
          <p className="mt-0.5 text-xs text-on-surface-variant">
            Configure automated multi-level email notifications and escalation timers per approval module.
          </p>
        </div>

        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-xs font-bold text-on-primary shadow-xs hover:bg-primary/90 transition disabled:opacity-50 self-start sm:self-auto"
          onClick={handleSave}
          disabled={saving}
        >
          <span className={`material-symbols-outlined text-base ${saving ? 'animate-spin' : ''}`}>
            {saving ? 'sync' : 'save'}
          </span>
          <span>{saving ? 'Saving Changes...' : 'Save Workflow Rules'}</span>
        </button>
      </section>

      {/* ── Workflow Module Selector Pills ── */}
      <div className="flex flex-wrap gap-2.5">
        {WORKFLOW_MODULES.map((mod) => {
          const isActive = activeModule === mod.code
          return (
            <button
              key={mod.code}
              type="button"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                isActive
                  ? 'bg-primary text-on-primary shadow-xs'
                  : 'bg-surface-container-low text-on-surface hover:bg-surface-container-high border border-outline-variant/30'
              }`}
              onClick={() => {
                setActiveModule(mod.code)
                setFeedback(null)
              }}
            >
              <span className="material-symbols-outlined text-sm">{mod.icon}</span>
              <span>{mod.name}</span>
            </button>
          )
        })}
      </div>

      {feedback && (
        <div
          className={`flex items-center gap-2.5 p-3.5 rounded-2xl text-xs font-semibold ${
            feedback.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-rose-50 text-rose-800 border border-rose-200'
          }`}
        >
          <span className="material-symbols-outlined text-base">
            {feedback.type === 'success' ? 'check_circle' : 'error'}
          </span>
          <span>{feedback.message}</span>
        </div>
      )}

      {/* ── Master Switch Card (Mobile App Configuration Style) ── */}
      <div
        className={`flex items-center justify-between gap-4 p-5 rounded-3xl border transition-all ${
          currentRule.isEnabled
            ? 'border-primary/30 bg-primary/[0.03]'
            : 'border-outline-variant/25 bg-surface-container-lowest'
        }`}
      >
        <div className="flex items-center gap-3.5">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl transition ${
              currentRule.isEnabled ? 'bg-primary/10 text-primary' : 'bg-surface-container-high text-on-surface-variant'
            }`}
          >
            <span className="material-symbols-outlined text-xl">
              {currentRule.isEnabled ? 'notifications_active' : 'notifications_off'}
            </span>
          </span>
          <div>
            <h3 className="font-display text-sm font-extrabold text-on-surface">
              Automated Notification Engine for {currentRule.moduleName || activeModule}
            </h3>
            <p className="text-[11px] text-on-surface-variant">
              Master switch controlling initial email alerts and multi-level escalations for this approval stream.
            </p>
          </div>
        </div>

        <div>
          <AppToggle
            checked={Boolean(currentRule.isEnabled)}
            onChange={handleToggleMasterEngine}
            ariaLabel={`Toggle ${currentRule.moduleName}`}
          />
        </div>
      </div>

      {/* ── Operational Termination Rule Banner ── */}
      <div className="flex items-start gap-3 p-4 rounded-2xl border border-primary/20 bg-primary/[0.04] text-xs text-on-surface">
        <span className="material-symbols-outlined text-primary text-lg shrink-0 mt-0.5">verified_user</span>
        <div className="leading-relaxed">
          <strong className="text-primary font-bold">Automatic Termination Rule:</strong> Once an approval request is approved or rejected by any authorized manager, all pending and future escalation reminders for that specific request are immediately and permanently terminated.
        </div>
      </div>

      {/* ── Stage 0: Initial Submission Notification ── */}
      <div
        className={`rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs transition-opacity ${
          !currentRule.notifyInitial || !currentRule.isEnabled ? 'opacity-65' : ''
        }`}
      >
        <div className="flex items-center justify-between border-b border-outline-variant/15 pb-4 mb-5">
          <div className="flex items-center gap-2.5">
            <span className="rounded-xl bg-blue-100 text-blue-800 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider">
              Stage 0
            </span>
            <h3 className="font-display text-sm font-extrabold text-on-surface">
              Initial Submission Notification
            </h3>
          </div>

          <div className="flex items-center gap-2.5">
            <span className="text-[11px] font-bold text-on-surface-variant">
              {currentRule.notifyInitial ? 'Active' : 'Disabled'}
            </span>
            <AppToggle
              checked={Boolean(currentRule.notifyInitial)}
              disabled={!currentRule.isEnabled}
              onChange={(nextVal) => handleRuleFieldChange('notifyInitial', nextVal)}
              ariaLabel="Toggle Stage 0"
            />
          </div>
        </div>

        {/* 12-Column Grid: Col 1 Timing (2 cols) | Col 2 Recipient (3 cols) | Col 3 Template (4 cols) | Col 4 CC (3 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-end">
          <div className="lg:col-span-2 flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              Timing
            </label>
            <div className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 py-2 text-xs font-bold text-primary flex items-center gap-1.5 h-[38px]">
              <span className="material-symbols-outlined text-base">bolt</span>
              <span>0h (Immediate)</span>
            </div>
          </div>

          <div className="lg:col-span-3 flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              Recipient Target
            </label>
            <AppDropdown
              value={currentRule.initialRecipientType || 'MANAGER'}
              onChange={(val) => handleRuleFieldChange('initialRecipientType', String(val))}
              options={RECIPIENT_OPTIONS}
              disabled={!currentRule.notifyInitial || !currentRule.isEnabled}
            />
          </div>

          <div className="lg:col-span-4 flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              Email Template
            </label>
            <AppDropdown
              value={currentRule.initialTemplateCode || ''}
              onChange={(val) => handleRuleFieldChange('initialTemplateCode', String(val))}
              options={templateOptions}
              placeholder="Select template..."
              disabled={!currentRule.notifyInitial || !currentRule.isEnabled}
            />
          </div>

          <div className="lg:col-span-3 flex flex-col gap-1.5 justify-center h-[38px]">
            <AppCheckbox
              id="initial_cc_req"
              checked={Boolean(currentRule.initialCcRequestor)}
              onChange={(val) => handleRuleFieldChange('initialCcRequestor', val)}
              disabled={!currentRule.notifyInitial || !currentRule.isEnabled}
              label="CC Submitter / Requestor"
            />
          </div>
        </div>

        {/* Sub-row for Specific User or Role */}
        {(currentRule.initialRecipientType === 'SPECIFIC_USER' || currentRule.initialRecipientType === 'SPECIFIC_ROLE') && (
          <div className="mt-4 pt-3.5 border-t border-outline-variant/15 grid grid-cols-1 lg:grid-cols-12 gap-5 items-end">
            <div className="lg:col-start-3 lg:col-span-4 flex flex-col gap-1.5">
              <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                {currentRule.initialRecipientType === 'SPECIFIC_USER' ? 'Select Specific User' : 'Select Specific Role'}
              </label>
              {currentRule.initialRecipientType === 'SPECIFIC_USER' ? (
                <AppCombobox
                  value={String(currentRule.initialRecipientUserId || '')}
                  onChange={(val) => handleRuleFieldChange('initialRecipientUserId', Number(val))}
                  options={userOptions}
                  placeholder="Choose user..."
                  disabled={!currentRule.notifyInitial || !currentRule.isEnabled}
                />
              ) : (
                <AppDropdown
                  value={String(currentRule.initialRecipientRoleId || '')}
                  onChange={(val) => handleRuleFieldChange('initialRecipientRoleId', Number(val))}
                  options={roleOptions}
                  placeholder="Choose role..."
                  disabled={!currentRule.notifyInitial || !currentRule.isEnabled}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Stage 1: Level 1 Escalation (L1) ── */}
      <div
        className={`rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs transition-opacity ${
          !currentRule.l1Enabled || !currentRule.isEnabled ? 'opacity-65' : ''
        }`}
      >
        <div className="flex items-center justify-between border-b border-outline-variant/15 pb-4 mb-5">
          <div className="flex items-center gap-2.5">
            <span className="rounded-xl bg-amber-100 text-amber-900 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider">
              Level 1 Escalation
            </span>
            <h3 className="font-display text-sm font-extrabold text-on-surface">
              L1 Notification (Primary Escalation)
            </h3>
          </div>

          <div className="flex items-center gap-2.5">
            <span className="text-[11px] font-bold text-on-surface-variant">
              {currentRule.l1Enabled ? 'Active' : 'Disabled'}
            </span>
            <AppToggle
              checked={Boolean(currentRule.l1Enabled)}
              disabled={!currentRule.isEnabled}
              onChange={(nextVal) => handleRuleFieldChange('l1Enabled', nextVal)}
              ariaLabel="Toggle Stage 1"
            />
          </div>
        </div>

        {/* 12-Column Grid: Col 1 Trigger (2 cols) | Col 2 Escalate To (3 cols) | Col 3 Template (4 cols) | Col 4 CC (3 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-end">
          <div className="lg:col-span-2 flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              Trigger (Hours)
            </label>
            <input
              type="number"
              min="1"
              max="720"
              className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 py-2 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary h-[38px]"
              value={currentRule.l1Hours ?? 4}
              onChange={(e) => handleRuleFieldChange('l1Hours', Number(e.target.value))}
              disabled={!currentRule.l1Enabled || !currentRule.isEnabled}
            />
          </div>

          <div className="lg:col-span-3 flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              Escalate To
            </label>
            <AppDropdown
              value={currentRule.l1RecipientType || 'MANAGER_OF_MANAGER'}
              onChange={(val) => handleRuleFieldChange('l1RecipientType', String(val))}
              options={RECIPIENT_OPTIONS}
              disabled={!currentRule.l1Enabled || !currentRule.isEnabled}
            />
          </div>

          <div className="lg:col-span-4 flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              Email Template
            </label>
            <AppDropdown
              value={currentRule.l1TemplateCode || ''}
              onChange={(val) => handleRuleFieldChange('l1TemplateCode', String(val))}
              options={templateOptions}
              placeholder="Select template..."
              disabled={!currentRule.l1Enabled || !currentRule.isEnabled}
            />
          </div>

          <div className="lg:col-span-3 flex flex-col gap-2">
            <AppCheckbox
              id="l1_cc_approver"
              checked={Boolean(currentRule.l1CcOriginalApprover)}
              onChange={(val) => handleRuleFieldChange('l1CcOriginalApprover', val)}
              disabled={!currentRule.l1Enabled || !currentRule.isEnabled}
              label="CC Original Approver"
            />
            <AppCheckbox
              id="l1_cc_req"
              checked={Boolean(currentRule.l1CcRequestor)}
              onChange={(val) => handleRuleFieldChange('l1CcRequestor', val)}
              disabled={!currentRule.l1Enabled || !currentRule.isEnabled}
              label="CC Submitter"
            />
          </div>
        </div>

        {/* Sub-row for Specific User or Role */}
        {(currentRule.l1RecipientType === 'SPECIFIC_USER' || currentRule.l1RecipientType === 'SPECIFIC_ROLE') && (
          <div className="mt-4 pt-3.5 border-t border-outline-variant/15 grid grid-cols-1 lg:grid-cols-12 gap-5 items-end">
            <div className="lg:col-start-3 lg:col-span-4 flex flex-col gap-1.5">
              <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                {currentRule.l1RecipientType === 'SPECIFIC_USER' ? 'Select Specific User' : 'Select Specific Role'}
              </label>
              {currentRule.l1RecipientType === 'SPECIFIC_USER' ? (
                <AppCombobox
                  value={String(currentRule.l1RecipientUserId || '')}
                  onChange={(val) => handleRuleFieldChange('l1RecipientUserId', Number(val))}
                  options={userOptions}
                  placeholder="Choose user..."
                  disabled={!currentRule.l1Enabled || !currentRule.isEnabled}
                />
              ) : (
                <AppDropdown
                  value={String(currentRule.l1RecipientRoleId || '')}
                  onChange={(val) => handleRuleFieldChange('l1RecipientRoleId', Number(val))}
                  options={roleOptions}
                  placeholder="Choose role..."
                  disabled={!currentRule.l1Enabled || !currentRule.isEnabled}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Stage 2: Level 2 Escalation (L2) ── */}
      <div
        className={`rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs transition-opacity ${
          !currentRule.l2Enabled || !currentRule.isEnabled ? 'opacity-65' : ''
        }`}
      >
        <div className="flex items-center justify-between border-b border-outline-variant/15 pb-4 mb-5">
          <div className="flex items-center gap-2.5">
            <span className="rounded-xl bg-orange-100 text-orange-900 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider">
              Level 2 Escalation
            </span>
            <h3 className="font-display text-sm font-extrabold text-on-surface">
              L2 Notification (Secondary Escalation)
            </h3>
          </div>

          <div className="flex items-center gap-2.5">
            <span className="text-[11px] font-bold text-on-surface-variant">
              {currentRule.l2Enabled ? 'Active' : 'Disabled'}
            </span>
            <AppToggle
              checked={Boolean(currentRule.l2Enabled)}
              disabled={!currentRule.isEnabled}
              onChange={(nextVal) => handleRuleFieldChange('l2Enabled', nextVal)}
              ariaLabel="Toggle Stage 2"
            />
          </div>
        </div>

        {/* 12-Column Grid: Col 1 Trigger (2 cols) | Col 2 Escalate To (3 cols) | Col 3 Template (4 cols) | Col 4 CC (3 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-end">
          <div className="lg:col-span-2 flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              Trigger (Hours)
            </label>
            <input
              type="number"
              min="1"
              max="720"
              className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 py-2 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary h-[38px]"
              value={currentRule.l2Hours ?? 8}
              onChange={(e) => handleRuleFieldChange('l2Hours', Number(e.target.value))}
              disabled={!currentRule.l2Enabled || !currentRule.isEnabled}
            />
          </div>

          <div className="lg:col-span-3 flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              Escalate To
            </label>
            <AppDropdown
              value={currentRule.l2RecipientType || 'DEPARTMENT_HEAD'}
              onChange={(val) => handleRuleFieldChange('l2RecipientType', String(val))}
              options={RECIPIENT_OPTIONS}
              disabled={!currentRule.l2Enabled || !currentRule.isEnabled}
            />
          </div>

          <div className="lg:col-span-4 flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              Email Template
            </label>
            <AppDropdown
              value={currentRule.l2TemplateCode || ''}
              onChange={(val) => handleRuleFieldChange('l2TemplateCode', String(val))}
              options={templateOptions}
              placeholder="Select template..."
              disabled={!currentRule.l2Enabled || !currentRule.isEnabled}
            />
          </div>

          <div className="lg:col-span-3 flex flex-col gap-2">
            <AppCheckbox
              id="l2_cc_approver"
              checked={Boolean(currentRule.l2CcOriginalApprover)}
              onChange={(val) => handleRuleFieldChange('l2CcOriginalApprover', val)}
              disabled={!currentRule.l2Enabled || !currentRule.isEnabled}
              label="CC Original Approver"
            />
            <AppCheckbox
              id="l2_cc_req"
              checked={Boolean(currentRule.l2CcRequestor)}
              onChange={(val) => handleRuleFieldChange('l2CcRequestor', val)}
              disabled={!currentRule.l2Enabled || !currentRule.isEnabled}
              label="CC Submitter"
            />
          </div>
        </div>

        {/* Sub-row for Specific User or Role */}
        {(currentRule.l2RecipientType === 'SPECIFIC_USER' || currentRule.l2RecipientType === 'SPECIFIC_ROLE') && (
          <div className="mt-4 pt-3.5 border-t border-outline-variant/15 grid grid-cols-1 lg:grid-cols-12 gap-5 items-end">
            <div className="lg:col-start-3 lg:col-span-4 flex flex-col gap-1.5">
              <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                {currentRule.l2RecipientType === 'SPECIFIC_USER' ? 'Select Specific User' : 'Select Specific Role'}
              </label>
              {currentRule.l2RecipientType === 'SPECIFIC_USER' ? (
                <AppCombobox
                  value={String(currentRule.l2RecipientUserId || '')}
                  onChange={(val) => handleRuleFieldChange('l2RecipientUserId', Number(val))}
                  options={userOptions}
                  placeholder="Choose user..."
                  disabled={!currentRule.l2Enabled || !currentRule.isEnabled}
                />
              ) : (
                <AppDropdown
                  value={String(currentRule.l2RecipientRoleId || '')}
                  onChange={(val) => handleRuleFieldChange('l2RecipientRoleId', Number(val))}
                  options={roleOptions}
                  placeholder="Choose role..."
                  disabled={!currentRule.l2Enabled || !currentRule.isEnabled}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Stage 3: Level 3 Escalation (L3) ── */}
      <div
        className={`rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs transition-opacity ${
          !currentRule.l3Enabled || !currentRule.isEnabled ? 'opacity-65' : ''
        }`}
      >
        <div className="flex items-center justify-between border-b border-outline-variant/15 pb-4 mb-5">
          <div className="flex items-center gap-2.5">
            <span className="rounded-xl bg-red-100 text-red-900 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider">
              Level 3 Escalation
            </span>
            <h3 className="font-display text-sm font-extrabold text-on-surface">
              L3 Notification (Executive / Admin Alert)
            </h3>
          </div>

          <div className="flex items-center gap-2.5">
            <span className="text-[11px] font-bold text-on-surface-variant">
              {currentRule.l3Enabled ? 'Active' : 'Disabled'}
            </span>
            <AppToggle
              checked={Boolean(currentRule.l3Enabled)}
              disabled={!currentRule.isEnabled}
              onChange={(nextVal) => handleRuleFieldChange('l3Enabled', nextVal)}
              ariaLabel="Toggle Stage 3"
            />
          </div>
        </div>

        {/* 12-Column Grid: Col 1 Trigger (2 cols) | Col 2 Escalate To (3 cols) | Col 3 Template (4 cols) | Col 4 CC (3 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-end">
          <div className="lg:col-span-2 flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              Trigger (Hours)
            </label>
            <input
              type="number"
              min="1"
              max="720"
              className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 py-2 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary h-[38px]"
              value={currentRule.l3Hours ?? 24}
              onChange={(e) => handleRuleFieldChange('l3Hours', Number(e.target.value))}
              disabled={!currentRule.l3Enabled || !currentRule.isEnabled}
            />
          </div>

          <div className="lg:col-span-3 flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              Escalate To
            </label>
            <AppDropdown
              value={currentRule.l3RecipientType || 'DEPARTMENT_HEAD'}
              onChange={(val) => handleRuleFieldChange('l3RecipientType', String(val))}
              options={RECIPIENT_OPTIONS}
              disabled={!currentRule.l3Enabled || !currentRule.isEnabled}
            />
          </div>

          <div className="lg:col-span-4 flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              Email Template
            </label>
            <AppDropdown
              value={currentRule.l3TemplateCode || ''}
              onChange={(val) => handleRuleFieldChange('l3TemplateCode', String(val))}
              options={templateOptions}
              placeholder="Select template..."
              disabled={!currentRule.l3Enabled || !currentRule.isEnabled}
            />
          </div>

          <div className="lg:col-span-3 flex flex-col gap-2">
            <AppCheckbox
              id="l3_cc_approver"
              checked={Boolean(currentRule.l3CcOriginalApprover)}
              onChange={(val) => handleRuleFieldChange('l3CcOriginalApprover', val)}
              disabled={!currentRule.l3Enabled || !currentRule.isEnabled}
              label="CC Original Approver"
            />
            <AppCheckbox
              id="l3_cc_req"
              checked={Boolean(currentRule.l3CcRequestor)}
              onChange={(val) => handleRuleFieldChange('l3CcRequestor', val)}
              disabled={!currentRule.l3Enabled || !currentRule.isEnabled}
              label="CC Submitter"
            />
          </div>
        </div>

        {/* Sub-row for Specific User or Role */}
        {(currentRule.l3RecipientType === 'SPECIFIC_USER' || currentRule.l3RecipientType === 'SPECIFIC_ROLE') && (
          <div className="mt-4 pt-3.5 border-t border-outline-variant/15 grid grid-cols-1 lg:grid-cols-12 gap-5 items-end">
            <div className="lg:col-start-3 lg:col-span-4 flex flex-col gap-1.5">
              <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                {currentRule.l3RecipientType === 'SPECIFIC_USER' ? 'Select Specific User' : 'Select Specific Role'}
              </label>
              {currentRule.l3RecipientType === 'SPECIFIC_USER' ? (
                <AppCombobox
                  value={String(currentRule.l3RecipientUserId || '')}
                  onChange={(val) => handleRuleFieldChange('l3RecipientUserId', Number(val))}
                  options={userOptions}
                  placeholder="Choose user..."
                  disabled={!currentRule.l3Enabled || !currentRule.isEnabled}
                />
              ) : (
                <AppDropdown
                  value={String(currentRule.l3RecipientRoleId || '')}
                  onChange={(val) => handleRuleFieldChange('l3RecipientRoleId', Number(val))}
                  options={roleOptions}
                  placeholder="Choose role..."
                  disabled={!currentRule.l3Enabled || !currentRule.isEnabled}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
